import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import GuideTemplate from '@/components/Approval/Common/GuideTemplate';
import useApprovalDocId from '@/hooks/approval/useApprovalDocId';
import useApprovalType from '@/hooks/approval/useApprovalType';
import useApprovalTypeGuide from '@/hooks/approval/useApprovalTypeGuide';
import { MyInfoState } from '@/recoil/atom';
import approvalDetailToFlow from '@/utils/approval/approvalDetailToFlow';
import {
  decodeSecurityLevel,
  SECU_LEVEL_ALL,
} from '@/utils/approval/securityLevel';
import { normalizeLang, pickI18nText } from '@/utils/approval/i18nText';
import {
  isContractApprovalType,
  parseContractIds,
} from '@/utils/approval/contractIds';
import { restoreLegacyAiDraftTags } from '@/utils/approval/aiDraftSection';
import { parseCancelTargetSnapshot } from '@/utils/approval/cancelApproval';
import { parseSaVoucherAttributes } from '@/utils/approval/saVoucher';
import type { SaTravelEntry } from '@/utils/approval/saTravelExpense';
import {
  isSapBondStatusType,
  parseSapBondStatusAttributes,
} from '@/utils/approval/sapBondStatus';
import {
  createProductionCostState,
  isProductionCostType,
  productionCostStateFromAttributes,
} from '@/utils/approval/productionCost';
import {
  createExternalLoanState,
  externalLoanStateFromAttributes,
  isExternalLoanType,
} from '@/utils/approval/externalLoan';
import {
  isDiscountVoucherType,
  parseDiscountVoucherAttributes,
} from '@/utils/approval/discountVoucher';
import {
  createExpenseVoucherState,
  isExpenseVoucherType,
  parseExpenseVoucherAttributes,
} from '@/utils/approval/expenseVoucher';
import {
  createExpenseState,
  expenseDataToState,
  isExpensePreApprovalType,
  parseExpenseData,
} from '@/utils/approval/expensePreApproval';
import {
  isOpenConstructionCostType,
  parseOpenConstructionCostAttributes,
} from '@/utils/approval/openConstructionCost';
import {
  isFncoPreMarketingType,
  parseFncoPreMarketingAttributes,
} from '@/utils/approval/fncoPreMarketing';
import {
  isMarketingExpenseType,
  parseMarketingExpenseAttributes,
} from '@/utils/approval/marketingExpense';
import {
  createEntExpenseState,
  entExpenseAttributesToState,
  isEntExpenseType,
  parseEntExpenseAttributes,
} from '@/utils/approval/entExpense';
import {
  checklistToOptionKeys,
  isWorkSpecificationType,
  parseWorkSpecificationChecklist,
} from '@/utils/approval/workSpecification';
import {
  FNCO_SHOW_ROOM_MISSING_MESSAGE,
  isFncoShowRoomType,
  parseFncoShowRoomRequestNumber,
} from '@/utils/approval/fncoShowRoom';
import {
  FNCO_PRODUCT_PURCHASE_MISSING_MESSAGE,
  isFncoProductPurchaseType,
  parseFncoProductPurchaseRequestNumber,
} from '@/utils/approval/fncoProductPurchase';
import { useSnackbar } from '@/hooks/global-tools';
import {
  isVehicleCostType,
  parseVehicleCostContent,
  toVehicleCostEmployee,
} from '@/utils/approval/vehicleCost';
import { buildVehicleCostContentHtml } from '@/components/Approval/@components/VehicleCost';
import {
  createResignationState,
  isResignationType,
  parseResignationData,
  resignationDataToState,
} from '@/utils/approval/resignation';
import {
  corporateSealAttributesToState,
  createCorporateSealState,
  isCorporateSealType,
  parseCorporateSealAttributes,
} from '@/utils/approval/corporateSeal';
import {
  isNewCorporateCardType,
  newCorporateCardStateFromMyInfo,
  newCorporateCardAttributesToState,
  parseNewCorporateCardAttributes,
} from '@/utils/approval/newCorporateCard';
import {
  ACCOUNTING_BP_TYPE,
  createBpState,
  isBpRegistrationType,
  parseBpFormState,
} from '@/utils/approval/businessPartnerRegistration';
import {
  createAccountingBpState,
  isAccountingBpType,
  parseAccountingBpSapDocumentNumber,
} from '@/utils/approval/accountingBusinessPartner';
import {
  createSapContractFeeState,
  isSapContractFeeType,
} from '@/utils/approval/sapContractFee';
import {
  createMortgageFeeState,
  isMortgageFeeType,
  mortgageFeeAttributesToState,
  parseMortgageFeeAttributes,
} from '@/utils/approval/mortgageFee';
import {
  createStoreOpenState,
  isStoreOpenType,
  parseStoreOpenAttributes,
  storeOpenAttributesToState,
} from '@/utils/approval/storeOpen';
import {
  corporateCardRequestAttributesToState,
  buildCorporateCardRequestSubject,
  corporateCardRequestStateFromMyInfo,
  isCorporateCardRequestType,
  parseCorporateCardRequestAttributes,
} from '@/utils/approval/corporateCardRequest';
import {
  annualLeaveRequestAttributesToState,
  annualLeaveRequestStateFromMyInfo,
  buildAnnualLeaveRequestSubject,
  isAnnualLeaveRequestType,
  leaveBalanceYearOf,
  parseAnnualLeaveRequestAttributes,
  toAnnualLeaveApplicant,
} from '@/utils/approval/annualLeaveRequest';
import {
  buildLongServiceLeaveSubject,
  isLongServiceLeaveType,
  longServiceLeaveDataToState,
  longServiceLeaveStateFromMyInfo,
  parseLongServiceLeaveData,
  toLongServiceApplicant,
} from '@/utils/approval/longServiceLeave';
import {
  isProbationEvaluationType,
  parseProbationEvaluation,
} from '@/utils/approval/probationEvaluation';
import {
  createSalesBrandPermissionState,
  isSalesBrandPermissionType,
  parseSalesBrandChanges,
} from '@/utils/approval/salesBrandPermission';
import {
  createSapCollateralChangeState,
  isSapCollateralType,
  parseSapCollateralSapKey,
} from '@/utils/approval/sapCollateralChange';
import {
  createInfluencerPplState,
  isInfluencerPplType,
  parseInfluencerPplContent,
  parseInfluencerPplPurpose,
  parseInfluencerPplSequence,
} from '@/utils/approval/influencerPpl';
import {
  createInfluencerMonthlyReportState,
  isInfluencerMonthlyReportType,
  parseInfluencerMonthlySequence,
} from '@/utils/approval/influencerMonthlyReport';
import {
  isGroupSalesType,
  parseGroupSalesAttributes,
} from '@/utils/approval/groupSales';
import {
  isGroupSalesMultiType,
  parseGroupSalesMultiAttributes,
} from '@/utils/approval/groupSalesMulti';
import {
  isMarketingCaseType,
  parseMarketingCaseAttributes,
} from '@/utils/approval/marketingCase';
import {
  parseSerpDocumentRef,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';
import {
  isSerpBulkStockAdjustmentType,
  parseSerpBulkDocumentRef,
} from '@/utils/approval/serpBulkStockAdjustment';
import {
  buildFncoPriceSupplySubject,
  createFncoPriceSupplyState,
  isFncoPriceType,
  parseFncoPriceSupplyRequestNumber,
} from '@/utils/approval/fncoPriceSupply';
import {
  isProductionVendorType,
  parseProductionVendorAttributes,
} from '@/utils/approval/productionVendor';
import {
  isProductLifecycleType,
  parseProductLifecycleAttributes,
} from '@/utils/approval/productLifecycle';
import {
  isStyleMasterPriceType,
  parseStyleMasterPriceAttributes,
} from '@/utils/approval/styleMasterPrice';
import {
  isProductionCostClosingType,
  parseProductionCostClosingAttributes,
} from '@/utils/approval/productionCostClosing';
import {
  isClaimClosingType,
  parseClaimClosingAttributes,
} from '@/utils/approval/claimClosing';
import {
  isInventoryReplenishmentType,
  parseInventoryReplenishmentAttributes,
} from '@/utils/approval/inventoryReplenishment';
import {
  isStorageMaterialOrderType,
  parseStorageMaterialOrderAttributes,
} from '@/utils/approval/storageMaterialOrder';
import {
  isSupplierEvaluationType,
  parseSupplierEvaluationAttributes,
} from '@/utils/approval/supplierEvaluation';
import {
  isPurchaseRequisitionType,
  parsePurchaseRequisitionAttributes,
} from '@/utils/approval/purchaseRequisition';
import {
  isCostQuotationType,
  parseCostQuotationAttributes,
} from '@/utils/approval/costQuotation';
import {
  isSalesContractType,
  parseSalesContractAttributes,
} from '@/utils/approval/salesContract';
import {
  isCreditNoteType,
  parseCreditNoteAttributes,
} from '@/utils/approval/creditNote';
import {
  isClaimNoteType,
  parseClaimNoteAttributes,
} from '@/utils/approval/claimNote';
import AiDraftSectionPlugin from '@/components/LexicalEditor/plugins/AiDraftSectionPlugin';
import ReplaceContentPlugin, {
  type ReplaceContentApi,
} from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import {
  buildFormDefaultSubject,
  needsWriterName,
} from '@/utils/approval/formSubject';
import type { ApprovalDetailFormState } from '@/utils/approval/approvalDetailToFormState';
import ApprovalLineSection from './ApprovalLineSection';
import AttachmentField from './AttachmentField';
import CancelTargetField from './CancelTargetField';
import FncoPriceSupplyField from './FncoPriceSupplyField';
import SapCollateralChangeField from './SapCollateralChangeField';
import AccountingBusinessPartnerField from './AccountingBusinessPartnerField';
import InfluencerPplField from './InfluencerPplField';
import InfluencerMonthlyReportField from './InfluencerMonthlyReportField';
import ProductionCostField from './ProductionCostField';
import ExternalLoanField from './ExternalLoanField';
import SapBudgetIncreaseField from './SapBudgetIncreaseField';
import { CANCEL_APPROVAL_TYPES } from '@/constant';
import { toFixedMemberIds } from '@/utils/approval/fixedApprovalMembers';
import ContentField from './ContentField';
import ContractLinkField from './ContractLinkField';
import CreateFooter from './CreateFooter';
import CreateHeader from './CreateHeader';
import EntExpenseField from './EntExpenseField';
import ExpensePreApprovalField from './ExpensePreApprovalField';
import HireKindField from './HireKindField';
import { buildBcoPartsContent } from './bcoPartsContent';
import CorporateSealField from './CorporateSealField';
import MortgageFeeField from './MortgageFeeField';
import StoreOpenField from './StoreOpenField';
import CorporateCardRequestField from './CorporateCardRequestField';
import AnnualLeaveRequestField from './AnnualLeaveRequestField';
import LongServiceLeaveField from './LongServiceLeaveField';
import OpenConstructionCostField from './OpenConstructionCostField';
import FncoPreMarketingField from './FncoPreMarketingField';
import MarketingExpenseField from './MarketingExpenseField';
import ReferrerSection from './ReferrerSection';
import SaVoucherField from './SaVoucherField';
import SapContractFeeField from './SapContractFeeField';
import GroupSalesField from './GroupSalesField';
import MarketingCaseField from './MarketingCaseField';
import GroupSalesMultiField from './GroupSalesMultiField';
import SerpStockAdjustmentField from './SerpStockAdjustmentField';
import SerpStoreChangeField from './SerpStoreChangeField';
import SerpStoreOpenField from './SerpStoreOpenField';
import SerpStoreReturnField from './SerpStoreReturnField';
import SerpLendingDelayField from './SerpLendingDelayField';
import SerpStoreClosureField from './SerpStoreClosureField';
import SerpIntermediaryReplaceField from './SerpIntermediaryReplaceField';
import SerpSellingPriceField from './SerpSellingPriceField';
import SerpSeasonPlanField from './SerpSeasonPlanField';
import SerpBulkStockAdjustmentField from './SerpBulkStockAdjustmentField';
import SerpSeasonEndField from './SerpSeasonEndField';
import SerpApStockTransferField from './SerpApStockTransferField';
import SapBondStatusField from './SapBondStatusField';
import SaTravelExpenseField from './SaTravelExpenseField';
import DiscountVoucherField from './DiscountVoucherField';
import ExpenseVoucherField from './ExpenseVoucherField';
import ProductionVendorField from './ProductionVendorField';
import ProductLifecycleField from './ProductLifecycleField';
import StyleMasterPriceField from './StyleMasterPriceField';
import ProductionCostClosingField from './ProductionCostClosingField';
import ClaimClosingField from './ClaimClosingField';
import InventoryReplenishmentField from './InventoryReplenishmentField';
import StorageMaterialOrderField from './StorageMaterialOrderField';
import SupplierEvaluationField from './SupplierEvaluationField';
import PurchaseRequisitionField from './PurchaseRequisitionField';
import CostQuotationField from './CostQuotationField';
import SalesContractField from './SalesContractField';
import CreditNoteField from './CreditNoteField';
import ClaimNoteField from './ClaimNoteField';
import RelatedApprovalField from './RelatedApprovalField';
import ResignationField from './ResignationField';
import NewCorporateCardField from './NewCorporateCardField';
import BusinessPartnerRegistrationField from './BusinessPartnerRegistrationField';
import ProbationEvaluationField from './ProbationEvaluationField';
import SalesBrandPermissionField from './SalesBrandPermissionField';
import SecuLevelSection from './SecuLevelSection';
import ShopManagerFeeField from './ShopManagerFeeField';
import SubjectField from './SubjectField';
import VehicleCostField from './VehicleCostField';
import WorkSpecificationField from './WorkSpecificationField';
import useExitGuard from './useExitGuard';
import useHoverScrollbar from '@/components/Approval/@hooks/useHoverScrollbar';
import {
  approvalCreateApprovalLineAtom,
  approvalCreateDeactivatedNamesAtom,
  approvalCreateReferrersAtom,
  approvalCreateUploadingAttachmentsAtom,
  contractDraftEditorApiAtom,
} from './atoms';
import {
  Container,
  Header,
  MainArea,
  MobileHeader,
  Sidebar,
  SidebarCard,
} from './styles';
import type { ApprovalCreateMode, ApprovalFormValues } from './types';

export type { ApprovalCreateMode };

type ApprovalCreateProps = {
  apvType: number;
  mode?: ApprovalCreateMode; // create 신규작성(default) | edit 편집
  documentId?: string; // edit 모드일 때 사용 — PUT path param + 헤더 표시
  initialFormState?: ApprovalDetailFormState; // edit 모드: 서버 저장값으로 폼 초기화. create 모드는 양식 응답 초기값만 적용.
  onSubmitSuccess?: (docId: string) => void; // 호스트(모달)가 제출 성공을 직접 처리할 때 사용. 미제공 시 페이지 라우팅.
  onExit?: () => void; // 호스트(모달)에서 뒤로가기/취소 등 제출 외 종료 시 호출. 페이지 라우팅 등은 호스트가 직접 처리.
  // 호스트(모달)가 닫기 전에 이탈 경고 여부를 물어볼 수 있게 판정 함수를 노출. commentDirtyRef 와 동일 패턴.
  exitGuardRef?: MutableRefObject<(() => boolean) | null>;
  /**
   * 계약 품의(117·118) 법틀 진입용 — URL(contract_id | Ep)로 받은 계약을 첫 건(기준 계약)으로 자동 등록.
   * 편집 모드는 이 값이 없으면 단건조회 attributes.contractIds 를 쓴다.
   */
  initialContractIds?: number[];
  /**
   * 근태 취소 품의(226/227/232/237/238/239) 진입용 — URL `doc` 의 취소 대상 원 품의 문서번호.
   * 편집 모드는 단건조회 attributes 스냅샷을 쓰므로 무시
   */
  initialCancelTargetDocId?: string;
  /** SA 전표(99)·회계거래처(101) 진입용 — URL `Ep` 의 SAP 묶음번호. 편집 모드는 단건조회 attributes 를 쓰므로 무시 */
  initialSapDocumentNo?: string;
  /** 매장 OPEN(233)·F&CO 마케팅 사전(221)·마케팅 비용(212) 진입용 — URL `analysisId`. 편집 모드는 단건조회 attributes 를 쓰므로 무시 */
  initialAnalysisId?: string;
  /** F&CO 쇼룸 출고(209)·가격 등록 공급가·할인율·판매가 요청(197~199)·신제품 발주(202)·운영제품 재발주(203) EAS 진입용 — URL `reqno`. 편집 모드는 단건조회 attributes 를 쓰므로 무시 */
  initialRequestNumber?: string;
  /** SA 신규 출장비 정산(177)·사전 신청(179) 진입용 — URL `sa_docno`·`sa_ztype`. 편집은 진입 값이 없어 조회 실패로 남음(레거시 동일) */
  initialSaTravel?: SaTravelEntry;
  /** SERP 재고실사 조정(169)·매장 변경(162)·매장 개점(161)·매장반품 요청(171)·여신유예신청(163)·폐점 정산(181)·중간관리자 교체 정산(182)·판매가 조정(160)·시즌운영계획(159)·재고실사 조정 다중(210)·시즌마감 조정(174)·AP 재고 이관 요청(170) 진입용 — URL `type_code`·`seq`. 편집 모드는 단건조회 attributes 를 쓰므로 무시 */
  initialSerpDocument?: SerpDocumentRef;
  /** SAP 담보 신규(154)·변경(155) 진입용 — URL `Ep` 의 sapKey. 편집 모드는 단건조회 attributes 를 쓰므로 무시 */
  initialSapKey?: string;
  /** 인플루언서 월마감 보고서(108)·PPL 정산(135) 진입용 — URL `Ep` 의 문서 일련번호. 편집 모드는 단건조회 attributes 를 쓰므로 무시 */
  initialInfluencerSequence?: string;
  /** 외담대 발행(217) SAP 진입용 — URL `issueDate`(YYYYMMDD) 를 YYYY-MM-DD 로. 편집 모드는 저장값을 쓰므로 무시 */
  initialIssueDate?: string;
};

const ApprovalCreate = ({
  apvType,
  mode = 'create',
  documentId,
  initialFormState,
  onSubmitSuccess,
  onExit,
  exitGuardRef,
  initialContractIds,
  initialCancelTargetDocId,
  initialSapDocumentNo,
  initialAnalysisId,
  initialRequestNumber,
  initialSaTravel,
  initialSerpDocument,
  initialSapKey,
  initialInfluencerSequence,
  initialIssueDate,
}: ApprovalCreateProps) => {
  const hoverScrollbarHandlers = useHoverScrollbar();
  const isEditMode = mode === 'edit';
  // create 모드는 microtime 기반 docId 신규 생성, edit 모드는 진입 시 documentId 그대로 사용.
  const newDocId = useApprovalDocId(isEditMode ? documentId : undefined);
  const docId = isEditMode ? documentId ?? '' : newDocId;

  // create 첫 POST 성공 후 docId 보관 — 이후 저장은 PUT 으로 전환 (edit 는 항상 PUT).
  const [savedDocumentId, setSavedDocumentId] = useState<string | null>(
    isEditMode ? documentId ?? null : null,
  );
  const effectiveDocumentId = documentId ?? savedDocumentId ?? undefined;
  const openedDocumentId = isEditMode ? documentId : undefined;
  const hasPersisted = Boolean(effectiveDocumentId);

  const { data: approvalType, error: approvalTypeError } =
    useApprovalType(apvType);
  // 같은 양식이어도 작성자 법인에 따라 가이드가 갈림
  const myInfo = useAtomValue(MyInfoState);
  const myCompanyCode = myInfo?.companyCode;
  const { guide } = useApprovalTypeGuide(apvType, myCompanyCode);
  const methods = useForm<ApprovalFormValues>({
    defaultValues: {
      docId,
      subject: '',
      content: '',
      secuLevel: SECU_LEVEL_ALL,
      restrictReferrer: false,
      relatedApprovals: { parents: [], children: [], related: [] },
      initialRelatedDocIds: [],
      deletedRelatedDocIds: [],
      attachments: [],
      showImagesBelowContent: false,
      contractIds: [],
      contractContent: null,
      cancelTarget: null,
      saVoucher: null,
      // 이탈 판정 기준선이 빈 견적 블록부터 잡히도록 첫 렌더에 채워 둠
      expensePreApproval: isExpensePreApprovalType(apvType)
        ? createExpenseState()
        : null,
      openConstructionCost: null,
      fncoPreMarketing: null,
      marketingExpense: null,
      entExpense: isEntExpenseType(apvType) ? createEntExpenseState() : null,
      workSpecChecklist: [],
      fncoShowRoomRequestNumber:
        isFncoShowRoomType(apvType) && !isEditMode
          ? initialRequestNumber ?? null
          : null,
      fncoPurchaseRequestNumber:
        isFncoProductPurchaseType(apvType) && !isEditMode
          ? initialRequestNumber ?? null
          : null,
      vehicleCostContent: null,
      resignation: null,
      // 이탈 판정 기준선이 내 인사 정보로 채운 상태부터 잡히도록 첫 렌더에 채워 둠
      newCorporateCard:
        isNewCorporateCardType(apvType) && !isEditMode
          ? newCorporateCardStateFromMyInfo(myInfo)
          : null,
      bpRegistration:
        isBpRegistrationType(apvType) || isAccountingBpType(apvType)
          ? createBpState()
          : null,
      accountingBp:
        isAccountingBpType(apvType) && !isEditMode
          ? createAccountingBpState(initialSapDocumentNo ?? null)
          : null,
      corporateSeal:
        isCorporateSealType(apvType) && !isEditMode
          ? createCorporateSealState()
          : null,
      shopManagerFee: null,
      sapContractFee: isSapContractFeeType(apvType)
        ? createSapContractFeeState()
        : null,
      serpStockAdjustment: null,
      serpStoreChange: null,
      serpStoreOpen: null,
      serpStoreReturn: null,
      serpLendingDelay: null,
      serpStoreClosure: null,
      serpIntermediaryReplace: null,
      serpSellingPrice: null,
      serpSeasonPlan: null,
      serpBulkStockAdjustment: null,
      serpSeasonEnd: null,
      serpApStockTransfer: null,
      mortgageFee:
        isMortgageFeeType(apvType) && !isEditMode
          ? createMortgageFeeState()
          : null,
      storeOpen:
        isStoreOpenType(apvType) && !isEditMode ? createStoreOpenState() : null,
      corporateCardRequest:
        isCorporateCardRequestType(apvType) && !isEditMode
          ? corporateCardRequestStateFromMyInfo(myInfo)
          : null,
      annualLeaveRequest:
        isAnnualLeaveRequestType(apvType) && !isEditMode
          ? annualLeaveRequestStateFromMyInfo(myInfo)
          : null,
      longServiceLeave:
        isLongServiceLeaveType(apvType) && !isEditMode
          ? longServiceLeaveStateFromMyInfo(myInfo)
          : null,
      probationEvaluation: null,
      salesBrandPermission:
        isSalesBrandPermissionType(apvType) && !isEditMode
          ? createSalesBrandPermissionState()
          : null,
      groupSales: null,
      groupSalesMulti: null,
      marketingCase: null,
      productionVendor: null,
      productLifecycle: null,
      styleMasterPrice: null,
      productionCostClosing: null,
      claimClosing: null,
      inventoryReplenishment: null,
      storageMaterialOrder: null,
      supplierEvaluation: null,
      purchaseRequisition: null,
      costQuotation: null,
      salesContract: null,
      creditNote: null,
      claimNote: null,
      sapBudgetIncrease: null,
      sapCollateralChange:
        isSapCollateralType(apvType) && !isEditMode
          ? createSapCollateralChangeState(initialSapKey ?? null)
          : null,
      influencerPpl:
        isInfluencerPplType(apvType) && !isEditMode
          ? createInfluencerPplState(initialInfluencerSequence ?? null)
          : null,
      influencerMonthlyReport:
        isInfluencerMonthlyReportType(apvType) && !isEditMode
          ? createInfluencerMonthlyReportState(
              initialInfluencerSequence ?? null,
            )
          : null,
      productionCost:
        isProductionCostType(apvType) && !isEditMode
          ? createProductionCostState()
          : null,
      externalLoan:
        isExternalLoanType(apvType) && !isEditMode
          ? createExternalLoanState({ issueDate: initialIssueDate ?? null })
          : null,
      sapBondStatus: null,
      saTravelExpense: null,
      fncoPriceSupply:
        isFncoPriceType(apvType) && !isEditMode
          ? createFncoPriceSupplyState(initialRequestNumber ?? null)
          : null,
      discountVoucher: null,
      expenseVoucher:
        isExpenseVoucherType(apvType) && !isEditMode
          ? createExpenseVoucherState()
          : null,
    },
  });

  // 양식 응답(신규) 또는 서버 저장값(편집)이 form 에 들어간 시점에만 ContentField 를 mount.
  const [contentReady, setContentReady] = useState(false);

  // 취소 품의는 원 품의명 기반 제목(useCancelTargetField)이 우선 — 자동 채움도 이탈 판정도
  // 양식 기본 제목을 쓰지 않는다. 한쪽만 빼면 cancelTarget 도착 전까지 오탐이 난다.
  const isCancelType = CANCEL_APPROVAL_TYPES.includes(apvType);
  const isSubjectPending = needsWriterName(apvType) && !myInfo?.name;
  const formDefaultSubject =
    isCancelType || isSubjectPending
      ? null
      : buildFormDefaultSubject(apvType, myInfo?.name ?? '') ??
        approvalType?.subject ??
        null;

  // 고정 항목은 BE 가 빈 객체를 섞어 내려줄 수 있어 공통 헬퍼로 걸러 id 만 추린다.
  const fixedApproverIds = useMemo(
    () => toFixedMemberIds(approvalType?.fixedApprovers ?? []),
    [approvalType?.fixedApprovers],
  );
  const fixedCoApproverIds = useMemo(
    () => toFixedMemberIds(approvalType?.fixedCoApprovers ?? []),
    [approvalType?.fixedCoApprovers],
  );
  const fixedReferrerIds = useMemo(
    () => toFixedMemberIds(approvalType?.fixedReferrers ?? []),
    [approvalType?.fixedReferrers],
  );

  const { shouldWarnOnExit, captureSaveBaseline } = useExitGuard({
    isEditMode,
    contentReady,
    methods,
    formDefaultSubject,
    formFixedApproverIds: fixedApproverIds,
    formFixedCoApproverIds: fixedCoApproverIds,
    formFixedReferrerIds: fixedReferrerIds,
  });

  // 모달 X 닫기는 페이지 unload 가 아니라 beforeunload 가 안 걸리므로, 호스트(ApprovalModal)가 이 판정을 ref 로 받아 처리.
  useEffect(() => {
    if (!exitGuardRef) return;
    exitGuardRef.current = shouldWarnOnExit;
    return () => {
      exitGuardRef.current = null;
    };
  }, [exitGuardRef, shouldWarnOnExit]);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (isEditMode) return;
    if (contentReady) return;
    if (approvalTypeError) {
      setContentReady(true);
      return;
    }
    if (!approvalType || isSubjectPending) return;
    // 응답이 늦게 와도 먼저 입력한 제목을 덮지 않도록 빈 값일 때만 채운다.
    if (formDefaultSubject && !methods.getValues('subject')?.trim()) {
      methods.setValue('subject', formDefaultSubject);
    }
    // (구)매장 OPEN(8) 템플릿은 대리점 양식 전용 — 「대리점 양식」을 누를 때만 본문에 넣음
    const contentTemplate =
      apvType === 66
        ? buildBcoPartsContent(t)
        : isStoreOpenType(apvType)
        ? null
        : approvalType.contentTemplate;
    if (contentTemplate) {
      methods.setValue('content', contentTemplate);
    }
    // 양식 기본 공개레벨은 편집 모드와 같은 규칙으로 디코딩한다.
    // 9(비공개)를 그대로 넣으면 라디오(1/5/7) 어디에도 매칭되지 않아 전부 미선택으로 보였다 (PORTAL-13910).
    const { secuLevel, restrictReferrer } = decodeSecurityLevel(
      approvalType.defaultSecurityLevel,
    );
    methods.setValue('secuLevel', secuLevel);
    methods.setValue('restrictReferrer', restrictReferrer);
    setContentReady(true);
  }, [
    isEditMode,
    contentReady,
    approvalType,
    approvalTypeError,
    isSubjectPending,
    formDefaultSubject,
    methods,
    apvType,
    t,
  ]);

  // v2 응답의 name 등은 {ko,en,zh} 객체 — hydrate 시 현재 lang 문자열로 좁혀야 렌더 안전.
  const snackbar = useSnackbar();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);
  const approvalTypeName = useMemo(
    () => pickI18nText(approvalType?.name, lang),
    [approvalType?.name, lang],
  );

  // 편집 모드: approvers[] → FlowEntry[] 1회 계산 — reference 유지로 hydrate effect 재실행 방지.
  const initialFlowHydrate = useMemo(
    () =>
      isEditMode && initialFormState?.approvers
        ? approvalDetailToFlow(initialFormState.approvers, lang)
        : null,
    [isEditMode, initialFormState?.approvers, lang],
  );

  const setReferrersAtom = useSetAtom(approvalCreateReferrersAtom);
  const setApprovalLineAtom = useSetAtom(approvalCreateApprovalLineAtom);
  const setDeactivatedNamesAtom = useSetAtom(
    approvalCreateDeactivatedNamesAtom,
  );
  const setUploadingAttachmentsAtom = useSetAtom(
    approvalCreateUploadingAttachmentsAtom,
  );

  // 재오픈 시 데이터 초기화 — atom 은 메모리 보관이라 언마운트 시 직접 비워야 잔여값이 안 남는다.
  useEffect(
    () => () => {
      setApprovalLineAtom({ approvers: [] });
      setDeactivatedNamesAtom([]);
      setReferrersAtom([]);
      setUploadingAttachmentsAtom([]);
    },
    [
      setApprovalLineAtom,
      setDeactivatedNamesAtom,
      setReferrersAtom,
      setUploadingAttachmentsAtom,
    ],
  );

  // 편집 모드: initialFormState 도착 시 1회만 적용 — 양식 자동채움 대신 서버 저장값 우선.
  const initialAppliedRef = useRef(false);
  useEffect(() => {
    if (!isEditMode) return;
    if (initialAppliedRef.current) return;
    if (!initialFormState) return;
    initialAppliedRef.current = true;
    methods.setValue('docId', initialFormState.docId);
    methods.setValue('subject', initialFormState.subject);
    methods.setValue(
      'content',
      isContractApprovalType(apvType)
        ? restoreLegacyAiDraftTags(initialFormState.content)
        : initialFormState.content,
    );
    if (
      isContractApprovalType(apvType) &&
      !methods.getValues('contractContent')
    ) {
      const saved = initialFormState.attributes?.contractContent;
      methods.setValue(
        'contractContent',
        typeof saved === 'string' && saved ? saved : null,
      );
    }
    methods.setValue('secuLevel', initialFormState.secuLevel);
    methods.setValue(
      'showImagesBelowContent',
      initialFormState.showImagesBelowContent,
    );
    methods.setValue('restrictReferrer', initialFormState.restrictReferrer);
    methods.setValue('attachments', initialFormState.attachments);
    if (isExpensePreApprovalType(apvType)) {
      const expenseData = parseExpenseData(initialFormState.attributes);
      if (expenseData) {
        methods.setValue('expensePreApproval', expenseDataToState(expenseData));
      }
    }
    if (isOpenConstructionCostType(apvType)) {
      methods.setValue(
        'openConstructionCost',
        parseOpenConstructionCostAttributes(initialFormState.attributes),
      );
    }
    if (isGroupSalesType(apvType)) {
      methods.setValue(
        'groupSales',
        parseGroupSalesAttributes(initialFormState.attributes),
      );
    }
    if (isGroupSalesMultiType(apvType)) {
      methods.setValue(
        'groupSalesMulti',
        parseGroupSalesMultiAttributes(initialFormState.attributes),
      );
    }
    if (isMarketingCaseType(apvType)) {
      methods.setValue(
        'marketingCase',
        parseMarketingCaseAttributes(initialFormState.attributes),
      );
    }
    if (isProductionVendorType(apvType)) {
      methods.setValue(
        'productionVendor',
        parseProductionVendorAttributes(initialFormState.attributes),
      );
    }
    if (isProductLifecycleType(apvType)) {
      methods.setValue(
        'productLifecycle',
        parseProductLifecycleAttributes(initialFormState.attributes),
      );
    }
    if (isStyleMasterPriceType(apvType)) {
      methods.setValue(
        'styleMasterPrice',
        parseStyleMasterPriceAttributes(initialFormState.attributes),
      );
    }
    if (isProductionCostClosingType(apvType)) {
      methods.setValue(
        'productionCostClosing',
        parseProductionCostClosingAttributes(initialFormState.attributes),
      );
    }
    if (isClaimClosingType(apvType)) {
      methods.setValue(
        'claimClosing',
        parseClaimClosingAttributes(initialFormState.attributes),
      );
    }
    if (isInventoryReplenishmentType(apvType)) {
      methods.setValue(
        'inventoryReplenishment',
        parseInventoryReplenishmentAttributes(initialFormState.attributes),
      );
    }
    if (isStorageMaterialOrderType(apvType)) {
      methods.setValue(
        'storageMaterialOrder',
        parseStorageMaterialOrderAttributes(initialFormState.attributes),
      );
    }
    if (isSupplierEvaluationType(apvType)) {
      methods.setValue(
        'supplierEvaluation',
        parseSupplierEvaluationAttributes(initialFormState.attributes),
      );
    }
    if (isPurchaseRequisitionType(apvType)) {
      methods.setValue(
        'purchaseRequisition',
        parsePurchaseRequisitionAttributes(initialFormState.attributes),
      );
    }
    if (isCostQuotationType(apvType)) {
      methods.setValue(
        'costQuotation',
        parseCostQuotationAttributes(initialFormState.attributes),
      );
    }
    if (isSalesContractType(apvType)) {
      methods.setValue(
        'salesContract',
        parseSalesContractAttributes(initialFormState.attributes),
      );
    }
    if (isCreditNoteType(apvType)) {
      methods.setValue(
        'creditNote',
        parseCreditNoteAttributes(initialFormState.attributes),
      );
    }
    if (isClaimNoteType(apvType)) {
      methods.setValue(
        'claimNote',
        parseClaimNoteAttributes(initialFormState.attributes),
      );
    }
    if (isFncoPreMarketingType(apvType)) {
      methods.setValue(
        'fncoPreMarketing',
        parseFncoPreMarketingAttributes(initialFormState.attributes),
      );
    }
    if (isMarketingExpenseType(apvType)) {
      methods.setValue(
        'marketingExpense',
        parseMarketingExpenseAttributes(initialFormState.attributes),
      );
    }
    if (isEntExpenseType(apvType)) {
      const saved = parseEntExpenseAttributes(initialFormState.attributes);
      if (saved) {
        methods.setValue('entExpense', entExpenseAttributesToState(saved));
      }
    }
    if (isWorkSpecificationType(apvType)) {
      methods.setValue(
        'workSpecChecklist',
        checklistToOptionKeys(
          parseWorkSpecificationChecklist(initialFormState.attributes) ?? [],
        ),
      );
    }
    if (isFncoShowRoomType(apvType)) {
      methods.setValue(
        'fncoShowRoomRequestNumber',
        parseFncoShowRoomRequestNumber(initialFormState.attributes),
      );
    }
    if (isFncoProductPurchaseType(apvType)) {
      methods.setValue(
        'fncoPurchaseRequestNumber',
        parseFncoProductPurchaseRequestNumber(initialFormState.attributes),
      );
    }
    if (isVehicleCostType(apvType)) {
      methods.setValue(
        'vehicleCostContent',
        parseVehicleCostContent(initialFormState.attributes),
      );
    }
    if (isResignationType(apvType)) {
      const saved = parseResignationData(initialFormState.attributes);
      if (saved) {
        methods.setValue('resignation', resignationDataToState(saved));
      }
    }
    if (isNewCorporateCardType(apvType)) {
      const saved = parseNewCorporateCardAttributes(
        initialFormState.attributes,
      );
      if (saved) {
        methods.setValue(
          'newCorporateCard',
          newCorporateCardAttributesToState(saved),
        );
      }
    }
    if (isBpRegistrationType(apvType)) {
      const saved = parseBpFormState(initialFormState.attributes, apvType);
      if (saved) methods.setValue('bpRegistration', saved);
    }
    // 거래처 표는 저장 묶음번호로 다시 조회하고 제목도 덮어씀(레거시 동일). 업체소싱정보는 V2 저장분만 복원
    if (isAccountingBpType(apvType)) {
      methods.setValue(
        'accountingBp',
        createAccountingBpState(
          parseAccountingBpSapDocumentNumber(initialFormState.attributes),
        ),
      );
      const saved = parseBpFormState(
        initialFormState.attributes,
        ACCOUNTING_BP_TYPE,
      );
      if (saved) methods.setValue('bpRegistration', saved);
    }
    // 저장 제목은 그대로 두고 셀렉트만 맞춤 — 제목은 셀렉트를 바꿀 때만 다시 씀
    if (isCorporateSealType(apvType)) {
      methods.setValue(
        'corporateSeal',
        corporateSealAttributesToState(
          parseCorporateSealAttributes(initialFormState.attributes),
        ),
      );
    }
    if (isStoreOpenType(apvType)) {
      methods.setValue(
        'storeOpen',
        storeOpenAttributesToState(
          parseStoreOpenAttributes(initialFormState.attributes),
        ),
      );
    }
    if (isMortgageFeeType(apvType)) {
      methods.setValue(
        'mortgageFee',
        mortgageFeeAttributesToState(
          parseMortgageFeeAttributes(initialFormState.attributes),
        ),
      );
    }
    if (isCorporateCardRequestType(apvType)) {
      const saved = parseCorporateCardRequestAttributes(
        initialFormState.attributes,
      );
      if (saved) {
        methods.setValue(
          'corporateCardRequest',
          corporateCardRequestAttributesToState(apvType, saved),
        );
        // 레거시 — 편집 진입 때마다 제목을 양식 기본 제목으로 덮어씀
        methods.setValue(
          'subject',
          buildCorporateCardRequestSubject(apvType, saved.employeeName),
        );
      }
    }
    if (isExpenseVoucherType(apvType)) {
      // 개편 이전 문서는 저장값이 없어 빈 양식으로 연다(레거시 편집 화면과 같음)
      methods.setValue(
        'expenseVoucher',
        parseExpenseVoucherAttributes(initialFormState.attributes) ??
          createExpenseVoucherState(),
      );
    }
    if (isProbationEvaluationType(apvType)) {
      methods.setValue(
        'probationEvaluation',
        parseProbationEvaluation(initialFormState.attributes),
      );
    }
    if (isSalesBrandPermissionType(apvType)) {
      methods.setValue(
        'salesBrandPermission',
        createSalesBrandPermissionState(
          parseSalesBrandChanges(initialFormState.attributes),
        ),
      );
    }
    if (isSapBondStatusType(apvType)) {
      methods.setValue(
        'sapBondStatus',
        parseSapBondStatusAttributes(initialFormState.attributes),
      );
    }
    // 레거시는 편집으로 다시 열어도 저장 제목 대신 고정 문구, 표는 저장 요청번호로 다시 조회
    if (isFncoPriceType(apvType)) {
      methods.setValue('subject', buildFncoPriceSupplySubject(apvType));
      methods.setValue(
        'fncoPriceSupply',
        createFncoPriceSupplyState(
          parseFncoPriceSupplyRequestNumber(initialFormState.attributes),
        ),
      );
    }
    // 표는 저장 sapKey 로 다시 조회하고 제목도 조회 결과로 덮어씀(레거시 동일)
    if (isSapCollateralType(apvType)) {
      methods.setValue(
        'sapCollateralChange',
        createSapCollateralChangeState(
          parseSapCollateralSapKey(initialFormState.attributes),
        ),
      );
    }
    // 리포트는 저장 일련번호로 다시 조회하고 제목도 덮어씀(레거시 동일). 목적만 저장 문구 유지
    if (isInfluencerPplType(apvType)) {
      methods.setValue(
        'influencerPpl',
        createInfluencerPplState(
          parseInfluencerPplSequence(initialFormState.attributes),
          parseInfluencerPplPurpose(
            parseInfluencerPplContent(initialFormState.attributes),
          ),
        ),
      );
    }
    // 표는 저장 일련번호로 다시 조회해 그 표로 재상신하고 제목도 덮어씀
    if (isInfluencerMonthlyReportType(apvType)) {
      methods.setValue(
        'influencerMonthlyReport',
        createInfluencerMonthlyReportState(
          parseInfluencerMonthlySequence(initialFormState.attributes),
        ),
      );
    }
    // 저장 마감 월로 다시 조회하고 차이내역 내용만 복원, 제목은 저장값 유지(레거시 동일)
    if (isProductionCostType(apvType)) {
      methods.setValue(
        'productionCost',
        productionCostStateFromAttributes(initialFormState.attributes),
      );
    }
    if (isExternalLoanType(apvType)) {
      methods.setValue(
        'externalLoan',
        externalLoanStateFromAttributes(initialFormState.attributes),
      );
    }
    if (isDiscountVoucherType(apvType)) {
      methods.setValue(
        'discountVoucher',
        parseDiscountVoucherAttributes(initialFormState.attributes),
      );
    }
    // content 반영 후 ContentField mount — 에디터가 저장 본문을 초기값으로 받도록.
    setContentReady(true);

    // 참조인 — referrers + additionalReferrers 합쳐 atom 에 1회 hydrate (신규 작성 잔여값 덮어씀).
    const allReferrers = [
      ...(initialFormState.referrers ?? []),
      ...(initialFormState.additionalReferrers ?? []),
    ];
    setReferrersAtom(
      allReferrers.map((r) => ({
        id: r.passportId,
        name: pickI18nText(r.name, lang),
        positionName: pickI18nText(r.positionName, lang),
        departmentName: r.departmentName ?? '',
        divisionName: pickI18nText(r.divisionName, lang),
        profileImageUrl: r.profileImageUrl ?? '',
      })),
    );
  }, [isEditMode, initialFormState, methods, setReferrersAtom, lang, apvType]);

  // 쇼룸 출고(209)는 레거시처럼 요청번호 없이 열리면 진입 시 안내 — 저장은 CreateFooter 가 막음
  const showRoomCheckedRef = useRef(false);
  useEffect(() => {
    if (!isFncoShowRoomType(apvType) || showRoomCheckedRef.current) return;
    if (isEditMode && !initialFormState) return;
    showRoomCheckedRef.current = true;
    if (!methods.getValues('fncoShowRoomRequestNumber')) {
      snackbar.showError({ message: t(FNCO_SHOW_ROOM_MISSING_MESSAGE) });
    }
  }, [apvType, isEditMode, initialFormState, methods, snackbar, t]);

  // 신제품 발주(202)·운영제품 재발주(203)도 요청번호 없이 열리면 진입 시 안내 — 저장은 CreateFooter 가 막음
  const purchaseCheckedRef = useRef(false);
  useEffect(() => {
    if (!isFncoProductPurchaseType(apvType) || purchaseCheckedRef.current)
      return;
    if (isEditMode && !initialFormState) return;
    purchaseCheckedRef.current = true;
    if (!methods.getValues('fncoPurchaseRequestNumber')) {
      snackbar.showError({ message: t(FNCO_PRODUCT_PURCHASE_MISSING_MESSAGE) });
    }
  }, [apvType, isEditMode, initialFormState, methods, snackbar, t]);

  // 차량유지비(93) — 레거시처럼 한 번 저장된 임직원 정보는 편집해도 그대로 둠
  useEffect(() => {
    if (!isVehicleCostType(apvType)) return;
    if (isEditMode && !initialFormState) return;
    if (methods.getValues('vehicleCostContent')) return;
    const employee = toVehicleCostEmployee(myInfo);
    if (!employee) return;
    methods.setValue(
      'vehicleCostContent',
      buildVehicleCostContentHtml(employee, t),
    );
  }, [apvType, isEditMode, initialFormState, myInfo, methods, t]);

  // 퇴직원(196) — 편집은 저장된 퇴직자 정보 그대로, 없을 때만 내 인사 정보로
  useEffect(() => {
    if (!isResignationType(apvType)) return;
    if (isEditMode && !initialFormState) return;
    if (methods.getValues('resignation')) return;
    const employee = toVehicleCostEmployee(myInfo);
    if (!employee) return;
    methods.setValue('resignation', createResignationState(employee));
  }, [apvType, isEditMode, initialFormState, myInfo, methods]);

  // 신규입사자 법인카드(204) — 편집은 저장된 신청자 정보 그대로, 없을 때만 내 인사 정보로(인사 정보가 늦게 오는 경우)
  useEffect(() => {
    if (!isNewCorporateCardType(apvType)) return;
    if (isEditMode && !initialFormState) return;
    if (methods.getValues('newCorporateCard')) return;
    const state = newCorporateCardStateFromMyInfo(myInfo);
    if (state) methods.setValue('newCorporateCard', state);
  }, [apvType, isEditMode, initialFormState, myInfo, methods]);

  // 법인카드 한도증액·재발급(206·207) — 204 와 같이 편집은 저장된 신청자 정보, 없을 때만 내 인사 정보로
  useEffect(() => {
    if (!isCorporateCardRequestType(apvType)) return;
    if (isEditMode && !initialFormState) return;
    if (methods.getValues('corporateCardRequest')) return;
    const state = corporateCardRequestStateFromMyInfo(myInfo);
    if (state) methods.setValue('corporateCardRequest', state);
  }, [apvType, isEditMode, initialFormState, myInfo, methods]);

  // 연차휴가(32) — 편집은 저장 기간을 복원하고 제목을 다시 씀(레거시 setData). 신청자는 내 인사 정보
  useEffect(() => {
    if (!isAnnualLeaveRequestType(apvType)) return;
    if (isEditMode && !initialFormState) return;
    if (methods.getValues('annualLeaveRequest')) return;
    const applicant = toAnnualLeaveApplicant(myInfo);
    if (!applicant) return;
    const saved = isEditMode
      ? parseAnnualLeaveRequestAttributes(initialFormState?.attributes)
      : null;
    if (!saved) {
      methods.setValue(
        'annualLeaveRequest',
        annualLeaveRequestStateFromMyInfo(myInfo),
      );
      return;
    }
    const state = annualLeaveRequestAttributesToState(applicant, saved);
    methods.setValue('annualLeaveRequest', state);
    methods.setValue('subject', buildAnnualLeaveRequestSubject(state));
  }, [apvType, isEditMode, initialFormState, myInfo, methods]);

  // F&CO 장기근속 포상휴가(240) — 편집은 저장 구분·기간·서약을 복원하고 제목을 다시 씀(레거시 applyServiceYearsRules). 신청자는 내 인사 정보
  useEffect(() => {
    if (!isLongServiceLeaveType(apvType)) return;
    if (isEditMode && !initialFormState) return;
    if (methods.getValues('longServiceLeave')) return;
    const applicant = toLongServiceApplicant(myInfo);
    if (!applicant) return;
    const saved = isEditMode
      ? parseLongServiceLeaveData(initialFormState?.attributes)
      : null;
    if (!saved) {
      methods.setValue(
        'longServiceLeave',
        longServiceLeaveStateFromMyInfo(myInfo),
      );
      return;
    }
    const state = longServiceLeaveDataToState(applicant, saved);
    methods.setValue('longServiceLeave', state);
    methods.setValue(
      'subject',
      buildLongServiceLeaveSubject(state.serviceYears, applicant),
    );
  }, [apvType, isEditMode, initialFormState, myInfo, methods]);

  const annualLeaveBalanceYear = useMemo(() => {
    if (!isEditMode || !isAnnualLeaveRequestType(apvType)) return undefined;
    const saved = parseAnnualLeaveRequestAttributes(
      initialFormState?.attributes,
    );
    return saved ? leaveBalanceYearOf(saved.startsAt) : undefined;
  }, [isEditMode, apvType, initialFormState?.attributes]);

  // 계약 품의 카드 복원 소스 — 법틀 진입(prop) 우선, 없으면 편집 저장값. 다른 유형에선 빈 배열
  const contractHydrateIds = useMemo(
    () =>
      initialContractIds?.length
        ? initialContractIds
        : parseContractIds(initialFormState?.attributes?.contractIds),
    [initialContractIds, initialFormState?.attributes],
  );

  // 취소 품의 편집 복원 소스 — 저장 attributes 스냅샷(재조회 없음). 다른 유형·신규 작성은 null
  const cancelTargetHydrate = useMemo(
    () =>
      isEditMode
        ? parseCancelTargetSnapshot(initialFormState?.attributes)
        : null,
    [isEditMode, initialFormState?.attributes],
  );

  // SA 전표 편집 복원 소스 — 저장 attributes 스냅샷. 다른 유형·신규 작성은 null
  const saVoucherHydrate = useMemo(() => {
    if (!isEditMode) return null;
    const parsed = parseSaVoucherAttributes(initialFormState?.attributes);
    return parsed
      ? {
          sapDocumentNo: parsed.sapDocumentNo,
          zSum: parsed.zSum,
          vouchers: parsed.vouchers,
        }
      : null;
  }, [isEditMode, initialFormState?.attributes]);

  // SAP 채권현황 편집 복원 소스 — 저장 조건·SAP 원문. 다른 유형·신규 작성은 null
  const sapBondStatusHydrate = useMemo(
    () =>
      isEditMode && isSapBondStatusType(apvType)
        ? parseSapBondStatusAttributes(initialFormState?.attributes)
        : null,
    [isEditMode, apvType, initialFormState?.attributes],
  );

  const serpDocument = useMemo(
    () =>
      isEditMode
        ? isSerpBulkStockAdjustmentType(apvType)
          ? parseSerpBulkDocumentRef(initialFormState?.attributes)
          : parseSerpDocumentRef(initialFormState?.attributes)
        : initialSerpDocument ?? null,
    [isEditMode, apvType, initialFormState?.attributes, initialSerpDocument],
  );

  // 계약 품의만 본문에 AI 초안 섹션 플러그인 — 플러그인이 에디터 API 를 atom 에 등록
  const setContractDraftEditorApi = useSetAtom(contractDraftEditorApiAtom);
  const [replaceContentApi, setReplaceContentApi] =
    useState<ReplaceContentApi | null>(null);
  const contentPlugins = isContractApprovalType(apvType) ? (
    <AiDraftSectionPlugin onApiChange={setContractDraftEditorApi} />
  ) : isAnnualLeaveRequestType(apvType) ||
    isStoreOpenType(apvType) ||
    isProductLifecycleType(apvType) ||
    isStyleMasterPriceType(apvType) ||
    isPurchaseRequisitionType(apvType) ||
    isCostQuotationType(apvType) ||
    isSalesContractType(apvType) ||
    isInventoryReplenishmentType(apvType) ||
    isCreditNoteType(apvType) ||
    isClaimNoteType(apvType) ||
    isClaimClosingType(apvType) ? (
    <ReplaceContentPlugin onApiChange={setReplaceContentApi} />
  ) : undefined;

  const renderForm = () => {
    switch (apvType) {
      // 본문이 특별한 유형만 여기에 추가
      case 117:
      case 118:
        return (
          <ContractLinkField
            apvType={apvType}
            initialContractIds={contractHydrateIds}
            // 법틀 진입분은 접근 불가 계약이면 등록 취소 + 빈 폼. 편집 저장값은 저장 시 보존해야 해서 유지
            discardInitialOnForbidden={!isEditMode}
            generateInitialDrafts={!isEditMode}
          />
        );
      case 226:
      case 227:
      case 232:
      case 237:
      case 238:
      case 239:
        return (
          <CancelTargetField
            apvType={apvType}
            entryDocId={isEditMode ? undefined : initialCancelTargetDocId}
            initialTarget={cancelTargetHydrate}
            isEditMode={isEditMode}
          />
        );
      case 99:
        return (
          <SaVoucherField
            entrySapDocumentNo={isEditMode ? undefined : initialSapDocumentNo}
            initialVoucher={saVoucherHydrate}
          />
        );
      case ACCOUNTING_BP_TYPE:
        return <AccountingBusinessPartnerField ready={contentReady} />;
      case 185:
        return (
          <SapBondStatusField
            initialState={sapBondStatusHydrate}
            isEditMode={isEditMode}
          />
        );
      case 177:
      case 179:
        return (
          <SaTravelExpenseField
            apvType={apvType}
            entry={isEditMode ? undefined : initialSaTravel}
          />
        );
      case 68:
        return <DiscountVoucherField />;
      case 111:
        return <ExpenseVoucherField />;
      case 205:
        return <ExpensePreApprovalField />;
      case 213:
        return <EntExpenseField />;
      case 233:
      case 234:
        return (
          <OpenConstructionCostField
            apvType={apvType}
            entryAnalysisId={isEditMode ? undefined : initialAnalysisId}
          />
        );
      case 221:
        return (
          <FncoPreMarketingField
            entryAnalysisId={isEditMode ? undefined : initialAnalysisId}
            isEditMode={isEditMode}
          />
        );
      case 212:
        return (
          <MarketingExpenseField
            entryAnalysisId={isEditMode ? undefined : initialAnalysisId}
            isEditMode={isEditMode}
          />
        );
      case 219:
      case 220:
        return <HireKindField apvType={apvType} />;
      case 183:
        return <WorkSpecificationField />;
      case 93:
        return <VehicleCostField />;
      case 196:
        return <ResignationField />;
      case 204:
        return <NewCorporateCardField />;
      case 200:
      case 201:
        return isBpRegistrationType(apvType) ? (
          <BusinessPartnerRegistrationField apvType={apvType} />
        ) : null;
      case 113:
        return <CorporateSealField />;
      case 157:
        return <ShopManagerFeeField />;
      case 167:
        return <SapContractFeeField />;
      case 169:
        // 편집은 저장값 도착 전 null 이라 '문서 정보 없음' 이 잠깐 보이지 않게 기다림
        return isEditMode && !initialFormState ? null : (
          <SerpStockAdjustmentField
            serpDocument={serpDocument}
            isEditMode={isEditMode}
          />
        );
      case 162:
        return isEditMode && !initialFormState ? null : (
          <SerpStoreChangeField
            serpDocument={serpDocument}
            isEditMode={isEditMode}
          />
        );
      case 161:
        return isEditMode && !initialFormState ? null : (
          <SerpStoreOpenField
            serpDocument={serpDocument}
            isEditMode={isEditMode}
          />
        );
      case 171:
        return isEditMode && !initialFormState ? null : (
          <SerpStoreReturnField serpDocument={serpDocument} />
        );
      case 159:
        return isEditMode && !initialFormState ? null : (
          <SerpSeasonPlanField serpDocument={serpDocument} />
        );
      case 163:
        return isEditMode && !initialFormState ? null : (
          <SerpLendingDelayField
            serpDocument={serpDocument}
            isEditMode={isEditMode}
          />
        );
      case 181:
        return isEditMode && !initialFormState ? null : (
          <SerpStoreClosureField
            serpDocument={serpDocument}
            isEditMode={isEditMode}
          />
        );
      case 182:
        return isEditMode && !initialFormState ? null : (
          <SerpIntermediaryReplaceField
            serpDocument={serpDocument}
            isEditMode={isEditMode}
          />
        );
      case 160:
        return isEditMode && !initialFormState ? null : (
          <SerpSellingPriceField serpDocument={serpDocument} />
        );
      case 210:
        return isEditMode && !initialFormState ? null : (
          <SerpBulkStockAdjustmentField
            serpDocument={serpDocument}
            isEditMode={isEditMode}
          />
        );
      case 174:
        return isEditMode && !initialFormState ? null : (
          <SerpSeasonEndField serpDocument={serpDocument} />
        );
      case 170:
        return isEditMode && !initialFormState ? null : (
          <SerpApStockTransferField serpDocument={serpDocument} />
        );
      case 81:
        return <MortgageFeeField />;
      case 8:
        return (
          <StoreOpenField
            editorApi={replaceContentApi}
            documentId={effectiveDocumentId}
            agencyTemplate={approvalType?.contentTemplate}
          />
        );
      case 206:
      case 207:
        return <CorporateCardRequestField apvType={apvType} />;
      case 32:
        return (
          <AnnualLeaveRequestField
            editorApi={replaceContentApi}
            balanceYear={annualLeaveBalanceYear}
          />
        );
      case 240:
        return <LongServiceLeaveField />;
      case 188:
        return <ProbationEvaluationField />;
      case 215:
        return <SalesBrandPermissionField />;
      case 61:
        return <GroupSalesField isEditMode={isEditMode} />;
      case 131:
        return <GroupSalesMultiField isEditMode={isEditMode} />;
      case 83:
        return <MarketingCaseField isEditMode={isEditMode} />;
      case 120:
        return <ProductionVendorField isEditMode={isEditMode} />;
      case 123:
        return (
          <ProductLifecycleField
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 124:
        return (
          <ProductionCostClosingField
            isEditMode={isEditMode}
            documentId={openedDocumentId}
          />
        );
      case 186:
        return (
          <ClaimClosingField
            isEditMode={isEditMode}
            documentId={openedDocumentId}
            editorApi={replaceContentApi}
          />
        );
      case 128:
        return (
          <StyleMasterPriceField
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 184:
        return (
          <InventoryReplenishmentField
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 125:
        return <StorageMaterialOrderField isEditMode={isEditMode} />;
      case 127:
        return <SupplierEvaluationField isEditMode={isEditMode} />;
      case 122:
      case 129:
        return (
          <PurchaseRequisitionField
            apvType={apvType}
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 121:
        return (
          <CostQuotationField
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 136:
        return (
          <SalesContractField
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 156:
        return (
          <CreditNoteField
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 166:
        return (
          <ClaimNoteField
            isEditMode={isEditMode}
            editorApi={replaceContentApi}
          />
        );
      case 187:
        return <SapBudgetIncreaseField ready={contentReady} />;
      case 154:
      case 155:
        return (
          <SapCollateralChangeField apvType={apvType} ready={contentReady} />
        );
      case 135:
        return <InfluencerPplField ready={contentReady} />;
      case 108:
        return <InfluencerMonthlyReportField ready={contentReady} />;
      case 216:
        return <ProductionCostField ready={contentReady} />;
      case 217:
        return <ExternalLoanField ready={contentReady} />;
      case 197:
      case 198:
      case 199:
        return <FncoPriceSupplyField apvType={apvType} ready={contentReady} />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Container {...hoverScrollbarHandlers}>
        {/* 모바일 헤더 (모바일에서만 노출) */}
        <MobileHeader>
          <CreateHeader
            approvalTypeName={approvalTypeName}
            mode={mode}
            stackDocId
          />
        </MobileHeader>

        {/* 좌측 메인 */}
        <MainArea>
          {/* PC 헤더 (PC에서만 노출) */}
          <Header>
            <CreateHeader approvalTypeName={approvalTypeName} mode={mode} />
          </Header>

          <SubjectField />

          {/* 양식 가이드 */}
          <GuideTemplate content={guide ?? ''} />

          {/* 품의별 추가 폼 영역 */}
          {renderForm()}

          <ContentField ready={contentReady} plugins={contentPlugins} />
          <RelatedApprovalField documentId={effectiveDocumentId} />
          <AttachmentField />
          <CreateFooter
            apvType={apvType}
            documentId={effectiveDocumentId}
            deactivatedCheckDocumentId={openedDocumentId}
            onPersisted={setSavedDocumentId}
            onSubmitSuccess={onSubmitSuccess}
            onExit={onExit}
            shouldWarnOnExit={shouldWarnOnExit}
            captureSaveBaseline={captureSaveBaseline}
          />
        </MainArea>

        {/* 우측 사이드바 */}
        <Sidebar>
          {/* 결재라인과 참조인은 최근 결재라인이 함께 갈아끼우는 한 덩어리 — 카드도 하나 */}
          <SidebarCard>
            <ApprovalLineSection
              documentId={effectiveDocumentId}
              deactivatedCheckDocumentId={openedDocumentId}
              apvType={apvType}
              initialFlow={initialFlowHydrate?.flow}
              initialOrderedCooperation={initialFlowHydrate?.orderedCooperation}
            />
            <ReferrerSection
              apvType={apvType}
              mode={hasPersisted ? 'E' : 'N'}
            />
          </SidebarCard>
          <SidebarCard>
            <SecuLevelSection />
          </SidebarCard>
        </Sidebar>
      </Container>
    </FormProvider>
  );
};

export default ApprovalCreate;
